import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';

import { sapiRepository } from '@repositories/sapi';

import { ApiContractType } from '@app-types/api/contract';
import { SapiProductType, SapiResponseType } from '@app-types/api/sapi';
import { ContractType } from '@app-types/contract';
import { UserType } from '@app-types/user';

import { PlanTypeId } from '@constants/plan';

class ContractsRepository {
    url: string;

    public constructor(url: string) {
        this.url = url;
    }

    public async getSignedContracts(sub: string, user: UserType): Promise<ContractType[]> {
        const { items } = await apiHttpService.get<{
            items: ApiContractType[];
        }>(`${this.url}/personnes/${sub}/contrats-signes`, undefined, {
            user,
        });

        const filteredItems = items.filter((offer) => offer.typeLigne === 'MOBILE');

        const { produits: offersDetails } = await this.getOffersDetails(
            filteredItems.map((contract) => contract.abonnement.idOffre),
            user,
        );

        return Promise.all(
            filteredItems.map(async (contract) => {
                const [iban, isRenewalEligible] = await Promise.all([
                    this.getIban(contract._links.compteFacturation.href, user),
                    this.getRenewalEligibility(contract.id, user),
                ]);
                const product = offersDetails[contract.abonnement.idOffre];

                return {
                    subscription: {
                        activationDate: contract.abonnement.dateActivation,
                        offerId: contract.abonnement.idOffre,
                        label: contract.abonnement.libelle,
                        mobileDataOffer: contract.abonnement.offreDataMobile,
                        isPremium: product?.type_id ? /plan_(premium|ideo)/.test(product.type_id) : false,
                        isSowo: product?.type_id ? product.type_id.includes(PlanTypeId.SOWO) : false,
                    },
                    statusChangeDate: contract.dateChangementStatut,
                    creationDate: contract.dateCreation,
                    id: contract.id,
                    marketLine: contract.ligneMarche,
                    phoneNumber: contract.numeroTel,
                    status: contract.statut,
                    lineType: contract.typeLigne,
                    user: {
                        rightsProfileLabel: contract.utilisateur.libelleProfilDroits,
                        rightsProfile: contract.utilisateur.profilDroits,
                    },
                    isRenewalEligible,
                    iban,
                    mobile: null,
                };
            }),
        );
    }

    public async getContract(sub: string, contractId: string, user: UserType): Promise<ContractType | undefined> {
        return this.getSignedContracts(sub, user).then((contracts) => {
            return contracts.find((contract) => contract.id === contractId);
        });
    }

    public async getPersonneFromContract(idContract: string, user: UserType): Promise<string | undefined> {
        return apiHttpService
            .get<{ _links: { titulaire: { href: string } } }>(`${this.url}/contrats/${idContract}`, undefined, {
                user,
            })
            .then((contract) => contract._links.titulaire.href.replace('/personnes/', ''))
            .catch(() => undefined);
    }

    private async getRenewalEligibility(contractId: string, user: UserType): Promise<boolean> {
        try {
            const { items } = await apiHttpService.get<{ items: { statut: boolean; renouvellementAutorise: true }[] }>(
                `${this.url}/contrats/${contractId}/eligibilites-renouvellement`,
                undefined,
                {
                    user,
                },
            );

            const contract = items?.[0];

            if (!contract) {
                return false;
            }

            return contract.statut && contract.renouvellementAutorise;
        } catch {
            return false;
        }
    }

    private async getIban(url: string, user: UserType): Promise<string | undefined> {
        try {
            const data = await apiHttpService.get<{
                compteBancaire?: {
                    iban: string;
                };
            }>(`${this.url}${url}`, undefined, {
                user,
            });

            return data.compteBancaire?.iban;
        } catch {
            return undefined;
        }
    }

    private async getOffersDetails(offers: string[], user: UserType): Promise<SapiResponseType<SapiProductType>> {
        try {
            if (offers.length > 0) {
                return await sapiRepository.getProductsByGencodes<SapiResponseType<SapiProductType>>(offers, user);
            } else {
                return { produits: {} };
            }
        } catch {
            return { produits: {} };
        }
    }
}

export const contractsRepository = new ContractsRepository(getAppConfig().b2r.url);
